import {
  TEXT_ANNOTATION_LINE_HEIGHT_FACTOR,
  TEXT_ANNOTATION_MIN_WIDTH,
  TEXT_ANNOTATION_MAX_WIDTH,
  CALLOUT_KNEE_LENGTH,
  CALLOUT_ARROW_LENGTH,
  CALLOUT_CLICK_OFFSET,
  CALLOUT_LINK_RESOLVED,
  CALLOUT_LINK_UNRESOLVED,
  SHEET_LINK_MIN_HIT_PX,
} from "./constants";

/**
 * Calculate distance between two points
 */
export function distance(p1, p2) {
  if (!p1 || !p2) return Infinity;
  return Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2));
}

/** Approximate average glyph width used for legacy content-sized hitboxes. */
export function estimateTextCharWidth(fontSize = 16) {
  return fontSize / 1.5;
}

let _measureCtx = null;

/** Pixel width of `text` at fontSize — prefers canvas measureText over char heuristics. */
export function measureAnnotationTextWidth(text, fontSize = 16) {
  const value = String(text ?? "");
  if (!value) return 0;
  try {
    if (typeof document !== "undefined") {
      if (!_measureCtx) {
        const canvas = document.createElement("canvas");
        _measureCtx = canvas.getContext("2d");
      }
      if (_measureCtx) {
        // Match viewer/export (Arial / sans-serif) rather than over-wide fontSize/1.5.
        _measureCtx.font = `${fontSize || 16}px Arial, sans-serif`;
        return _measureCtx.measureText(value).width;
      }
    }
  } catch (err) {
    /* fall through */
  }
  // Tighter than /1.5 so wrap doesn't leave a large empty right gutter.
  return value.length * (fontSize || 16) * 0.5;
}

function hardBreakWord(word, fontSize, usableWidth) {
  const chunks = [];
  let chunk = "";
  for (const ch of word) {
    const next = chunk + ch;
    if (measureAnnotationTextWidth(next, fontSize) <= usableWidth || !chunk) {
      chunk = next;
    } else {
      chunks.push(chunk);
      chunk = ch;
    }
  }
  if (chunk) chunks.push(chunk);
  return chunks.length ? chunks : [""];
}

/**
 * Wrap annotation text to a box width. Honors explicit newlines and soft-wraps
 * long lines at word boundaries when possible.
 *
 * When boxWidth is null/undefined (legacy annotations), do not soft-wrap — only
 * split on explicit newlines so historical text keeps its original layout.
 */
export function wrapAnnotationText(content, fontSize = 16, boxWidth) {
  const text = String(content ?? "");

  // Legacy annotations predate boxWidth; never force them into the default 200px box.
  if (boxWidth == null) {
    const lines = text.split("\n");
    return lines.length ? lines : [""];
  }

  // Match editor chrome: 2px border + 4px padding each side (see TextAnnotationInput).
  const usableWidth = Math.max(TEXT_ANNOTATION_MIN_WIDTH, boxWidth) - 12;
  const lines = [];

  for (const paragraph of text.split("\n")) {
    if (paragraph === "") {
      lines.push("");
      continue;
    }

    const words = paragraph.split(" ");
    let current = "";
    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      const test = current ? `${current} ${word}` : word;
      if (measureAnnotationTextWidth(test, fontSize) <= usableWidth) {
        current = test;
        continue;
      }
      if (current) {
        lines.push(current);
        current = "";
      }
      if (measureAnnotationTextWidth(word, fontSize) <= usableWidth) {
        current = word;
      } else {
        const pieces = hardBreakWord(word, fontSize, usableWidth);
        // All but last piece are full lines; last piece starts the next line.
        for (let p = 0; p < pieces.length - 1; p++) lines.push(pieces[p]);
        current = pieces[pieces.length - 1] || "";
      }
    }
    lines.push(current);
  }

  return lines.length ? lines : [""];
}

/**
 * Layout metrics for a text annotation box.
 * With boxWidth: fixed width, height grows with wrapped lines. A persisted
 * boxHeight keeps an intentionally taller user-resized box from collapsing.
 * Without boxWidth (legacy): content-sized width, no soft wrap — matches pre-NCK-517.
 */
export function getTextAnnotationMetrics(
  content,
  fontSize = 16,
  boxWidth,
  boxHeight,
) {
  const size = fontSize || 16;
  const lines = wrapAnnotationText(content, size, boxWidth);

  if (boxWidth == null) {
    const charWidth = estimateTextCharWidth(size);
    const longest = Math.max(0, ...lines.map((line) => line.length));
    // Same formula the viewer used before boxWidth existed.
    const width = (longest + 1) * charWidth;
    const lineHeight = size * 1.5;
    const height = Math.max(lineHeight, lines.length * lineHeight);
    return { lines, width, height, lineHeight };
  }

  const width = Math.max(TEXT_ANNOTATION_MIN_WIDTH, boxWidth);
  const lineHeight = size * TEXT_ANNOTATION_LINE_HEIGHT_FACTOR;
  const naturalHeight = Math.max(lineHeight, lines.length * lineHeight);
  const height =
    boxHeight == null ? naturalHeight : Math.max(naturalHeight, boxHeight);

  return { lines, width, height, lineHeight };
}

/**
 * Box width to use when opening the text editor.
 * Legacy annotations (no boxWidth) get content-sized width so editing doesn't
 * suddenly force a 200px wrap.
 */
export function resolveTextAnnotationBoxWidth(annotation) {
  if (annotation?.boxWidth != null) return annotation.boxWidth;
  return getTextAnnotationMetrics(
    annotation?.content,
    annotation?.fontSize || 16,
    undefined,
  ).width;
}

export function getCalloutBox(annotation) {
  const fontSize = annotation?.fontSize || 16;
  const { lines, width, lineHeight } = getTextAnnotationMetrics(
    annotation?.content,
    fontSize,
    annotation?.boxWidth,
  );
  const height = Math.max(
    fontSize * 1.5,
    lines.length * lineHeight + 8,
    annotation?.boxHeight || 0,
  );
  return {
    left: annotation.position.x - width / 2,
    top: annotation.position.y - height / 2,
    width,
    height,
  };
}

export function getCalloutBoxCenter(tip, end, width) {
  const target =
    distance(tip, end) < 5
      ? { x: tip.x + CALLOUT_CLICK_OFFSET, y: tip.y - CALLOUT_CLICK_OFFSET }
      : end;
  const side = target.x >= tip.x ? 1 : -1;
  return { x: target.x + (side * width) / 2, y: target.y };
}

function getCalloutAttachPoint(point, box) {
  const right = box.left + box.width;
  const bottom = box.top + box.height;
  const cx = box.left + box.width / 2;
  const cy = box.top + box.height / 2;
  if (point.x < box.left) return { x: box.left, y: cy, horizontal: true };
  if (point.x > right) return { x: right, y: cy, horizontal: true };
  if (point.y < box.top) return { x: cx, y: box.top, horizontal: false };
  if (point.y > bottom) return { x: cx, y: bottom, horizontal: false };
  return null;
}

function getNearestCalloutSide(point, box) {
  const right = box.left + box.width;
  const bottom = box.top + box.height;
  const cx = box.left + box.width / 2;
  const cy = box.top + box.height / 2;
  const sides = [
    { distance: point.y - box.top, x: cx, y: box.top },
    { distance: bottom - point.y, x: cx, y: bottom },
    { distance: point.x - box.left, x: box.left, y: cy },
    { distance: right - point.x, x: right, y: cy },
  ];
  const nearest = sides.reduce((a, b) => (b.distance < a.distance ? b : a));
  return { x: nearest.x, y: nearest.y };
}

export function getCalloutLeaderGeometry(tip, box, customKnee) {
  if (!tip || !box) return null;
  let attach = customKnee ? getCalloutAttachPoint(customKnee, box) : null;
  let knee = attach ? customKnee : null;
  if (!attach) {
    attach = getCalloutAttachPoint(tip, box);
    if (!attach) {
      attach = getNearestCalloutSide(tip, box);
      knee = attach;
    }
  }
  if (!knee) {
    const dx = tip.x - attach.x;
    const dy = tip.y - attach.y;
    knee = attach.horizontal
      ? { x: attach.x + Math.sign(dx) * Math.min(CALLOUT_KNEE_LENGTH, Math.abs(dx) / 2), y: attach.y }
      : { x: attach.x, y: attach.y + Math.sign(dy) * Math.min(CALLOUT_KNEE_LENGTH, Math.abs(dy) / 2) };
  }
  const angle = Math.atan2(tip.y - knee.y, tip.x - knee.x);
  const headLength = Math.min(CALLOUT_ARROW_LENGTH, distance(tip, knee));
  const spread = Math.PI / 7;
  const arrow = [
    tip,
    {
      x: tip.x - headLength * Math.cos(angle - spread),
      y: tip.y - headLength * Math.sin(angle - spread),
    },
    {
      x: tip.x - headLength * Math.cos(angle + spread),
      y: tip.y - headLength * Math.sin(angle + spread),
    },
  ];
  return { points: [tip, knee, attach], arrow };
}

export function getCalloutKneePoint(annotation) {
  if (!annotation?.calloutTip) return null;
  const geometry = getCalloutLeaderGeometry(
    annotation.calloutTip,
    getCalloutBox(annotation),
    annotation.calloutKnee,
  );
  return geometry ? geometry.points[1] : null;
}

export function resizeCalloutBox(startBox, handle, pt, annotation) {
  const fontSize = annotation.fontSize || 16;
  const fromWest = handle.includes("w");
  const fromNorth = handle.includes("n");
  const anchorX = fromWest ? startBox.left + startBox.width : startBox.left;
  const anchorY = fromNorth ? startBox.top + startBox.height : startBox.top;
  const width = Math.min(
    TEXT_ANNOTATION_MAX_WIDTH,
    Math.max(TEXT_ANNOTATION_MIN_WIDTH, fromWest ? anchorX - pt.x : pt.x - anchorX),
  );
  const { lines, lineHeight } = getTextAnnotationMetrics(
    annotation.content,
    fontSize,
    width,
  );
  const minHeight = Math.max(fontSize * 1.5, lines.length * lineHeight + 8);
  const height = Math.max(minHeight, fromNorth ? anchorY - pt.y : pt.y - anchorY);
  const left = fromWest ? anchorX - width : anchorX;
  const top = fromNorth ? anchorY - height : anchorY;
  return {
    position: { x: left + width / 2, y: top + height / 2 },
    boxWidth: width,
    boxHeight: height > minHeight + 0.5 ? height : undefined,
  };
}

export function toSvgPoints(points, scale = 1) {
  return points.map((p) => `${p.x * scale},${p.y * scale}`).join(" ");
}

/**
 * Normalize a cloud segment to [x1, y1, x2, y2] for path rendering.
 */
export function normalizeCloudSegment(segment) {
  const { start, end } = segment;
  return start.x < end.x
    ? [start.x, start.y, end.x, end.y]
    : [end.x, end.y, start.x, start.y];
}

/**
 * Return all line segments for a cloud annotation (multi-segment or legacy single).
 */
export function getCloudSegments(annotation) {
  if (Array.isArray(annotation?.segments) && annotation.segments.length > 0) {
    return annotation.segments;
  }
  if (annotation?.start && annotation?.end) {
    return [{ start: annotation.start, end: annotation.end }];
  }
  return [];
}

const CLOUD_CONNECT_EPSILON = 1;

function getCloudEndpointPoints(annotation) {
  return getCloudSegments(annotation).flatMap((segment) => [
    segment.start,
    segment.end,
  ]);
}

function cloudAnnotationsShareMetadata(ann1, ann2) {
  return (
    ann1.color === ann2.color &&
    ann1.strokeWidth === ann2.strokeWidth &&
    (ann1.user?.id ?? null) === (ann2.user?.id ?? null)
  );
}

function cloudAnnotationsConnect(ann1, ann2) {
  if (!cloudAnnotationsShareMetadata(ann1, ann2)) {
    return false;
  }
  const pts1 = getCloudEndpointPoints(ann1);
  const pts2 = getCloudEndpointPoints(ann2);
  return pts1.some((p1) =>
    pts2.some((p2) => distance(p1, p2) < CLOUD_CONNECT_EPSILON),
  );
}

/**
 * Find all annotation indices that belong to the same connected cloud shape.
 */
export function findConnectedCloudAnnotationIndices(annotations, index) {
  const target = annotations[index];
  if (!target || target.shapeType !== "cloud") {
    return [index];
  }
  if (Array.isArray(target.segments) && target.segments.length > 0) {
    return [index];
  }
  if (target.cloudGroupId) {
    return annotations.reduce((indices, ann, i) => {
      if (ann.cloudGroupId === target.cloudGroupId) {
        indices.push(i);
      }
      return indices;
    }, []);
  }

  const visited = new Set([index]);
  const queue = [index];
  const indices = [index];

  while (queue.length > 0) {
    const currentIndex = queue.shift();
    const current = annotations[currentIndex];
    annotations.forEach((other, otherIndex) => {
      if (
        visited.has(otherIndex) ||
        other.page !== target.page ||
        other.shapeType !== "cloud" ||
        (Array.isArray(other.segments) && other.segments.length > 0)
      ) {
        return;
      }
      if (cloudAnnotationsConnect(current, other)) {
        visited.add(otherIndex);
        queue.push(otherIndex);
        indices.push(otherIndex);
      }
    });
  }

  return indices.sort((a, b) => a - b);
}

/**
 * Merge in-progress cloud edge annotations into a single multi-segment annotation.
 */
export function mergeCloudGroupAnnotations(annotations, groupId) {
  const groupItems = annotations.filter(
    (annotation) =>
      annotation.shapeType === "cloud" && annotation.cloudGroupId === groupId,
  );
  if (groupItems.length === 0) {
    return annotations;
  }
  if (groupItems.length === 1) {
    const only = groupItems[0];
    if (Array.isArray(only.segments) && only.segments.length > 0) {
      return annotations;
    }
    return annotations.map((annotation) =>
      annotation.cloudGroupId === groupId
        ? {
            ...annotation,
            segments: [{ start: annotation.start, end: annotation.end }],
            start: undefined,
            end: undefined,
            cloudGroupId: undefined,
          }
        : annotation,
    );
  }

  const segments = groupItems.map((annotation) => ({
    start: annotation.start,
    end: annotation.end,
  }));
  const base = groupItems[0];
  const merged = {
    id: base.id,
    type: "shape",
    shapeType: "cloud",
    segments,
    color: base.color,
    strokeWidth: base.strokeWidth,
    page: base.page,
    user: base.user,
  };

  let inserted = false;
  return annotations.reduce((acc, annotation) => {
    if (annotation.cloudGroupId !== groupId) {
      acc.push(annotation);
      return acc;
    }
    if (!inserted) {
      acc.push(merged);
      inserted = true;
    }
    return acc;
  }, []);
}

/**
 * Generate cloud path SVG string
 */
export function getCloudPath(x1, y1, x2, y2, amplitude = 10, wavelength = 16) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const length = Math.sqrt(dx * dx + dy * dy);
  const angle = Math.atan2(dy, dx);
  const numWaves = Math.max(1, Math.round(length / wavelength));
  const points = [];
  for (let i = 0; i <= numWaves; i++) {
    const t = i / numWaves;
    const baseX = x1 + dx * t;
    const baseY = y1 + dy * t;

    const offset = i % 2 === 0 ? -amplitude : amplitude;
    const nx = -Math.sin(angle);
    const ny = Math.cos(angle);
    const px = baseX + nx * offset;
    const py = baseY + ny * offset;
    points.push([px, py, i % 2 === 0 ? "peak" : "valley"]);
  }
  let d = `M${points[0][0]},${points[0][1]}`;
  let i = 1;
  while (i < points.length) {
    if (points[i][2] === "valley" && i > 0 && i < points.length - 1) {
      const next = points[i + 1];
      d += ` Q${points[i][0]},${points[i][1]} ${next[0]},${next[1]}`;
      i += 2;
    } else {
      d += ` L${points[i][0]},${points[i][1]}`;
      i++;
    }
  }
  return d;
}

/**
 * Convert SVG string to PNG buffer
 */
export const svgToPngBuffer = async (svg, width, height) => {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const svgBlob = new Blob([svg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(svgBlob);
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        if (!blob) return reject(new Error("Canvas toBlob failed"));
        const reader = new FileReader();
        reader.onload = () => {
          resolve(reader.result);
          URL.revokeObjectURL(url);
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(blob);
      }, "image/png");
    };
    img.onerror = reject;
    img.src = url;
  });
};

function annotationBox(ann) {
  if (!ann?.start || !ann?.end) return null;
  const x0 = Math.min(ann.start.x, ann.end.x);
  const y0 = Math.min(ann.start.y, ann.end.y);
  const x1 = Math.max(ann.start.x, ann.end.x);
  const y1 = Math.max(ann.start.y, ann.end.y);
  return { x0, y0, x1, y1, cx: (x0 + x1) / 2, cy: (y0 + y1) / 2 };
}

function isEmptyConnection(ann) {
  if (ann?.type !== "connection") return false;
  if (ann.linkedRfiId || ann.rfiTag || ann.linkedRfiTagId) return false;
  if (ann.photoTag || ann.linkedPhotoUrl || ann.linkedPhoto || ann.linkedPhotoPreviewUrl) {
    return false;
  }
  return true;
}

function boxesOverlapOrNear(a, b) {
  const ix0 = Math.max(a.x0, b.x0);
  const iy0 = Math.max(a.y0, b.y0);
  const ix1 = Math.min(a.x1, b.x1);
  const iy1 = Math.min(a.y1, b.y1);
  if (ix1 > ix0 && iy1 > iy0) {
    const inter = (ix1 - ix0) * (iy1 - iy0);
    const areaA = Math.max((a.x1 - a.x0) * (a.y1 - a.y0), 1);
    const areaB = Math.max((b.x1 - b.x0) * (b.y1 - b.y0), 1);
    if (inter / Math.min(areaA, areaB) >= 0.25) return true;
  }
  const dx = a.cx - b.cx;
  const dy = a.cy - b.cy;
  return dx * dx + dy * dy <= 60 * 60;
}

/** Drawing link stored as a connection. */
export function isDrawingLink(ann) {
  return (
    !!ann &&
    ann.type === "connection" &&
    !!(ann.targetDrawingNumber || ann.targetDrawingId)
  );
}

export function isAutoGeneratedDrawingLink(ann) {
  return isDrawingLink(ann) && !!ann.autoGenerated;
}

export function getCalloutLinkColor(ann) {
  return ann?.targetDrawingId ? CALLOUT_LINK_RESOLVED : CALLOUT_LINK_UNRESOLVED;
}

export function getSheetLinkLabel(ann) {
  const number = ann?.targetDrawingCode || ann?.targetDrawingNumber || "Sheet";
  return ann?.targetDrawingTitle ? `${number} — ${ann.targetDrawingTitle}` : number;
}

export function getSheetLinkTooltip(ann) {
  if (ann?.targetDrawingId) return `Go to ${getSheetLinkLabel(ann)}`;
  return `${ann?.targetDrawingCode || ann?.targetDrawingNumber || "Sheet"} — not found in this folder`;
}

export function getSheetLinkHitRadius(ann, screenScale = 1) {
  const minRadius = SHEET_LINK_MIN_HIT_PX / 2 / Math.max(screenScale || 1, 0.1);
  const box = annotationBox(ann);
  if (!box) return minRadius;
  return Math.max((box.x1 - box.x0) / 2, (box.y1 - box.y0) / 2, 6, minRadius);
}

function normalizeDrawingNumber(value) {
  return String(value || "")
    .replace(/[\s.\-_/]/g, "")
    .toUpperCase();
}

export function mergeDetectedSheetLinks(existing, added) {
  const current = Array.isArray(existing) ? existing : [];
  const incoming = Array.isArray(added) ? added : [];
  const seen = new Set(current.map((ann) => ann?.id).filter(Boolean));
  const toAdd = incoming.filter((ann) => {
    if (!ann?.id || seen.has(ann.id)) return false;
    seen.add(ann.id);
    return true;
  });
  return toAdd.length ? [...current, ...toAdd] : current;
}

export function applyResolvedSheetLinkTargets(existing, serverAnns) {
  const byId = new Map((serverAnns || []).map((ann) => [ann?.id, ann]));
  return (existing || []).map((ann) => {
    const fresh = byId.get(ann?.id);
    if (!fresh || !isDrawingLink(ann)) return ann;
    if (
      fresh.targetDrawingCode &&
      (!("targetDrawingCode" in ann) || ann.targetDrawingCode === fresh.targetDrawingCode)
    ) {
      return {
        ...ann,
        targetDrawingCode: fresh.targetDrawingCode,
        targetDrawingNumber: fresh.targetDrawingNumber,
        targetDrawingTitle: fresh.targetDrawingTitle,
        targetDrawingId: fresh.targetDrawingId,
        targetFolderId: fresh.targetFolderId,
      };
    }
    if (
      normalizeDrawingNumber(fresh.targetDrawingNumber) !==
      normalizeDrawingNumber(ann.targetDrawingNumber)
    ) {
      return ann;
    }
    return {
      ...ann,
      targetDrawingId: fresh.targetDrawingId,
      targetFolderId: fresh.targetFolderId,
    };
  });
}

/** Drop leftover empty connection boxes that sit on a sheet link (No Link duplicates). */
export function stripEmptyConnectionsOnSheetLinks(annotations) {
  if (!Array.isArray(annotations) || annotations.length === 0) return annotations || [];
  const sheetBoxes = annotations
    .filter((ann) => ann?.type === "sheet_link")
    .map((ann) => ({ ann, box: annotationBox(ann) }))
    .filter((item) => item.box);
  if (sheetBoxes.length === 0) return annotations;
  return annotations.filter((ann) => {
    if (!isEmptyConnection(ann)) return true;
    const box = annotationBox(ann);
    if (!box) return true;
    const page = ann.page ?? 0;
    return !sheetBoxes.some(
      (sheet) => (sheet.ann.page ?? 0) === page && boxesOverlapOrNear(box, sheet.box),
    );
  });
}

